import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Schedule as ScheduleIcon,
  Link as LinkIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { dynamicContentService } from '../../services/dynamicContentService';
import ExhibitionRewriteReview from './ExhibitionRewriteReview';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const DynamicContentPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [content, setContent] = useState<any[]>([]);
  const [sources, setSources] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState<any>(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [statusCounts, setStatusCounts] = useState<any>({});
  const [schedulerStatus, setSchedulerStatus] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState('generated');
  const [batches, setBatches] = useState<any[]>([]);

  useEffect(() => {
    loadContent();
    loadSources();
    loadStats();
    checkSchedulerStatus();
    loadBatches();
  }, []);

  useEffect(() => {
    // Auto-refresh batches every 10 seconds if there are active ones
    const activeBatches = batches.filter(b => b.status === 'processing');
    if (activeBatches.length > 0) {
      const interval = setInterval(() => {
        loadBatches();
        loadContent();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [batches]);

  const loadContent = async (status?: string) => {
    setLoading(true);
    setError(null);
    try {
      const filters = status ? { status } : { status: filterStatus };
      const data = await dynamicContentService.getContent(filters);
      setContent(data.content);
    } catch (err: any) {
      setError(err.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const loadSources = async () => {
    try {
      const data = await dynamicContentService.getSources();
      setSources(data.sources);
    } catch (err) {
      console.error('Error loading sources:', err);
    }
  };

  const loadStats = async () => {
    try {
      const data = await dynamicContentService.getStats();
      setStatusCounts(data.stats.content.byStatus || {});
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const checkSchedulerStatus = async () => {
    try {
      const status = await dynamicContentService.getSchedulerStatus();
      setSchedulerStatus(status.scheduler);
    } catch (err) {
      console.error('Error checking scheduler:', err);
    }
  };

  const loadBatches = async () => {
    try {
      const data = await dynamicContentService.getBatches();
      setBatches(data.batches || []);
    } catch (err) {
      console.error('Error loading batches:', err);
    }
  };

  const handleTriggerCheck = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await dynamicContentService.triggerCheck();
      setSuccess(result.message || 'Check triggered successfully');
      setTimeout(() => loadContent(), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to trigger check');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (contentId: number, newStatus: string) => {
    try {
      await dynamicContentService.updateContentStatus(contentId, newStatus);
      setSuccess(`Content ${newStatus} successfully`);
      loadContent();
      loadStats();
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    }
  };

  const handleEdit = (item: any) => {
    setEditDialog(item);
    setEditedDescription(item.description);
  };

  const handleSaveEdit = async () => {
    if (!editDialog) return;
    
    try {
      await dynamicContentService.editContent(editDialog.id, {
        description: editedDescription,
        editorEmail: 'editor@example.com', // TODO: Get from auth
        editReason: 'Manual edit'
      });
      setSuccess('Content updated successfully');
      setEditDialog(null);
      loadContent();
    } catch (err: any) {
      setError(err.message || 'Failed to save edit');
    }
  };

  const handleSchedulerToggle = async () => {
    try {
      if (schedulerStatus?.running) {
        await dynamicContentService.stopScheduler();
        setSuccess('Scheduler stopped');
      } else {
        await dynamicContentService.startScheduler();
        setSuccess('Scheduler started');
      }
      setTimeout(checkSchedulerStatus, 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to toggle scheduler');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'generated': return 'info';
      case 'reviewed': return 'warning';
      case 'approved': return 'success';
      case 'published': return 'primary';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const ContentCard = ({ item }: { item: any }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" component="h3">
            {item.title}
          </Typography>
          <Chip 
            label={item.status} 
            color={getStatusColor(item.status)} 
            size="small"
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {item.source?.source_name} • Valid: {new Date(item.valid_from).toLocaleDateString()} - {new Date(item.valid_until).toLocaleDateString()}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 2 }}>
          {item.description}
        </Typography>
        
        {/* Show if this exhibition was extended */}
        {item.metadata?.extendedOn && (
          <Box sx={{ mb: 1 }}>
            <Chip 
              label="Extended" 
              size="small" 
              color="info" 
              variant="outlined"
              title={`End date extended on ${new Date(item.metadata.extendedOn).toLocaleDateString()}`}
            />
          </Box>
        )}
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {item.source_urls?.map((url: string, idx: number) => (
            <Chip
              key={idx}
              icon={<LinkIcon />}
              label={`Source ${idx + 1}`}
              size="small"
              component="a"
              href={url}
              target="_blank"
              clickable
              variant="outlined"
            />
          ))}
        </Box>
        
      </CardContent>
      
      <CardActions>
        {item.status === 'generated' && (
          <>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleEdit(item)}
            >
              Edit
            </Button>
            <Button
              size="small"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={() => handleStatusChange(item.id, 'approved')}
            >
              Approve
            </Button>
          </>
        )}
        {item.status === 'approved' && (
          <Button
            size="small"
            color="primary"
            onClick={() => handleStatusChange(item.id, 'published')}
          >
            Publish
          </Button>
        )}
      </CardActions>
    </Card>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Dynamic Content Management
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            icon={schedulerStatus?.running ? <ScheduleIcon /> : <StopIcon />}
            label={schedulerStatus?.running ? 'Scheduler Running' : 'Scheduler Stopped'}
            color={schedulerStatus?.running ? 'success' : 'default'}
          />
          <Button
            variant="outlined"
            startIcon={schedulerStatus?.running ? <StopIcon /> : <StartIcon />}
            onClick={handleSchedulerToggle}
          >
            {schedulerStatus?.running ? 'Stop' : 'Start'} Scheduler
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleTriggerCheck}
            disabled={loading}
          >
            Check Now
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Batch Progress Display */}
      {batches.filter(b => b.status === 'processing').length > 0 && (
        <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            🔄 Active Content Generation
          </Typography>
          {batches.filter(b => b.status === 'processing').map((batch) => (
            <Box key={batch.batch_id} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Batch {batch.batch_id.slice(-8)} • {batch.source_count} sources
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 'bold' }}>
                  Processing...
                </Typography>
              </Box>
              <Box sx={{ 
                height: 6, 
                bgcolor: 'rgba(255,255,255,0.2)', 
                borderRadius: 3,
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  height: '100%', 
                  bgcolor: 'white', 
                  borderRadius: 3,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 0.6 },
                    '50%': { opacity: 1 },
                    '100%': { opacity: 0.6 }
                  }
                }} />
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5, display: 'block' }}>
                Using Claude Batch API • Estimated completion: 24 hours
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, v) => setTabValue(v)}
          sx={{
            '& .MuiTabs-indicator': {
              display: 'none' // Remove the ugly purple line
            },
            '& .MuiTab-root': {
              borderRadius: '8px 8px 0 0',
              marginRight: 1,
              '&.Mui-selected': {
                backgroundColor: 'rgba(123, 97, 255, 0.1)',
                color: '#cc081c'
              }
            }
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>New Content</span>
                {statusCounts.generated > 0 && (
                  <Chip 
                    label={statusCounts.generated} 
                    size="small" 
                    color="info"
                    sx={{ height: 20, minWidth: 20 }}
                  />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={statusCounts.approved || 0} color="success">
                <span>Approved</span>
              </Badge>
            } 
          />
          <Tab label="Sources" />
          <Tab label="Rewrite Review" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <FormControl sx={{ mb: 2, minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              loadContent(e.target.value);
            }}
            label="Filter by Status"
          >
            <MenuItem value="generated">New (Generated)</MenuItem>
            <MenuItem value="reviewed">Reviewed</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="">All</MenuItem>
          </Select>
        </FormControl>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {content.length === 0 ? (
              <Alert severity="info">
                No content found. Click "Check Now" to fetch new exhibitions.
              </Alert>
            ) : (
              // Group by museum
              (() => {
                const grouped: Record<string, any[]> = content.reduce((acc, item) => {
                  const museum = item.source?.source_name || 'Unknown';
                  if (!acc[museum]) acc[museum] = [];
                  acc[museum].push(item);
                  return acc;
                }, {} as Record<string, any[]>);
                
                return Object.entries(grouped)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([museum, items]) => (
                    <Box key={museum} sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                        {museum} ({items.length})
                      </Typography>
                      {items.map((item: any) => (
                        <ContentCard key={item.id} item={item} />
                      ))}
                    </Box>
                  ));
              })()
            )}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {content.filter(c => c.status === 'approved').map((item) => (
          <ContentCard key={item.id} item={item} />
        ))}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={2}>
          {sources.map((source) => {
            const nextCheck = source.next_check_at ? new Date(source.next_check_at) : null;
            const isOverdue = nextCheck && nextCheck < new Date();
            const daysUntilCheck = nextCheck ? Math.ceil((nextCheck.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;
            
            return (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={source.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6">{source.source_name}</Typography>
                      <Chip 
                        label={source.is_active ? 'Active' : 'Inactive'} 
                        color={source.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    
                    {/* Smart scheduling info */}
                    <Box sx={{ mb: 2 }}>
                      {nextCheck ? (
                        <Typography variant="body2" color={isOverdue ? 'error' : 'text.secondary'}>
                          {isOverdue ? (
                            <>🔄 Due for check (overdue)</>
                          ) : daysUntilCheck === 0 ? (
                            <>📅 Next check: Today</>
                          ) : daysUntilCheck === 1 ? (
                            <>📅 Next check: Tomorrow</>
                          ) : (
                            <>📅 Next check: {daysUntilCheck} days ({nextCheck.toLocaleDateString()})</>
                          )}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          📅 Scheduled for initial check
                        </Typography>
                      )}
                      
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        💡 Smart scheduling: checks 1 week before exhibitions expire
                      </Typography>
                    </Box>
                    
                    <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                      <a href={source.source_url} target="_blank" rel="noopener noreferrer">
                        {source.source_url}
                      </a>
                    </Typography>
                    
                    {source.last_checked_at && (
                      <Typography variant="caption" color="text.secondary">
                        Last checked: {new Date(source.last_checked_at).toLocaleString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <ExhibitionRewriteReview />
      </TabPanel>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Content</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={editedDescription}
            onChange={(e) => setEditedDescription(e.target.value)}
            margin="normal"
            label="Description"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DynamicContentPage;